/**
 * Wrappers around the trait mutations that pick the Hasura or custom-resolver
 * operation based on appUseNewTraitsEndpoints. Each takes the custom-resolver
 * variables and returns its response shape, so call sites only change which
 * hook they import.
 */

import {
  type CreateTraitMutation,
  type CreateTraitMutationVariables,
  type UpdateTraitDefinitionMutationVariables,
  useArchiveTraitLegacyMutation,
  useArchiveTraitMutation,
  useCreateTraitLegacyMutation,
  useCreateTraitMutation,
  useDeleteTraitLegacyMutation,
  useDeleteTraitMutation,
  useRestoreTraitLegacyMutation,
  useRestoreTraitMutation,
  useUpdateTraitDefinitionMutation,
  useUpdateTraitWithHasuraMutation,
} from "src/graphql";
import { useNewTraitsEndpoints } from "src/hooks/use-new-traits-endpoints";
import {
  asNewShape,
  toLegacyCreateInput,
  toLegacyUpdateInput,
} from "./dual-path";

/**
 * The subset of react-query mutation options the trait call sites use.
 * Callback arguments are deliberately omitted: they would differ between the
 * two paths, and no call site reads them.
 */
export type TraitMutationOptions = {
  onSuccess?: () => void;
};

export const useCreateTrait = (options?: TraitMutationOptions) => {
  const shouldUseNewEndpoints = useNewTraitsEndpoints();
  const newMutation = useCreateTraitMutation(options);
  const legacyMutation = useCreateTraitLegacyMutation(options);
  const active = shouldUseNewEndpoints ? newMutation : legacyMutation;

  return {
    ...active,
    mutateAsync: async (
      variables: CreateTraitMutationVariables,
    ): Promise<CreateTraitMutation> => {
      if (shouldUseNewEndpoints) {
        return await newMutation.mutateAsync(variables);
      }

      const { insert_trait_definitions_one: created } =
        await legacyMutation.mutateAsync({
          input: toLegacyCreateInput(variables.input),
        });
      if (!created) {
        // Hasura returns null only when the inserted row falls outside the
        // caller's select permission, which the create form can't act on.
        throw new Error("Trait was created but could not be read back");
      }
      return {
        createTraitDefinition:
          asNewShape<CreateTraitMutation["createTraitDefinition"]>(created),
      };
    },
  };
};

export const useUpdateTraitDefinition = (options?: TraitMutationOptions) => {
  const shouldUseNewEndpoints = useNewTraitsEndpoints();
  const newMutation = useUpdateTraitDefinitionMutation(options);
  const legacyMutation = useUpdateTraitWithHasuraMutation(options);
  const active = shouldUseNewEndpoints ? newMutation : legacyMutation;

  return {
    ...active,
    mutateAsync: async (variables: UpdateTraitDefinitionMutationVariables) => {
      if (shouldUseNewEndpoints) {
        return await newMutation.mutateAsync(variables);
      }
      return await legacyMutation.mutateAsync({
        id: variables.id,
        input: toLegacyUpdateInput(variables.input),
      });
    },
  };
};

export const useDeleteTrait = (options?: TraitMutationOptions) => {
  const shouldUseNewEndpoints = useNewTraitsEndpoints();
  const newMutation = useDeleteTraitMutation(options);
  const legacyMutation = useDeleteTraitLegacyMutation(options);
  return shouldUseNewEndpoints ? newMutation : legacyMutation;
};

export const useArchiveTrait = (options?: TraitMutationOptions) => {
  const shouldUseNewEndpoints = useNewTraitsEndpoints();
  const newMutation = useArchiveTraitMutation(options);
  const legacyMutation = useArchiveTraitLegacyMutation(options);
  return shouldUseNewEndpoints ? newMutation : legacyMutation;
};

export const useRestoreTrait = (options?: TraitMutationOptions) => {
  const shouldUseNewEndpoints = useNewTraitsEndpoints();
  const newMutation = useRestoreTraitMutation(options);
  const legacyMutation = useRestoreTraitLegacyMutation(options);
  return shouldUseNewEndpoints ? newMutation : legacyMutation;
};
